import { useEffect, useState } from "react";
import { ShieldCheck, Check } from "lucide-react";
import { CheckoutButton } from "./CheckoutButton";
import { useSite, useImages } from "@/context/SiteContent";

const devices = ["desktop", "tablet", "mobile"];

const tiltMove = (e) => {
  const el = e.currentTarget, r = el.getBoundingClientRect();
  const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
  el.style.setProperty("--tx", `${(-y * 9).toFixed(2)}deg`); el.style.setProperty("--ty", `${(x * 12).toFixed(2)}deg`);
  el.style.setProperty("--gx", `${((x + 0.5) * 100).toFixed(1)}%`); el.style.setProperty("--gy", `${((y + 0.5) * 100).toFixed(1)}%`);
};
const tiltLeave = (e) => { e.currentTarget.style.setProperty("--tx", "0deg"); e.currentTarget.style.setProperty("--ty", "0deg"); };

const DeviceShowcase = () => {
  const images = useImages();
  const cover = images.cover;
  const [active, setActive] = useState("desktop");
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setActive((a) => devices[(devices.indexOf(a) + 1) % devices.length]), 3600);
    return () => clearInterval(t);
  }, [paused, active]);

  return (
    <div className="device-stage" id="deviceStage" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="device-stage">
      <div aria-label="View on different platforms" className="device-tabs" role="tablist">
        {devices.map((d) => (
          <button key={d} role="tab" aria-selected={active === d} className={`device-tab${active === d ? " is-active" : ""}`} onClick={() => setActive(d)} type="button" data-testid={`device-tab-${d}`}>
            {d[0].toUpperCase() + d.slice(1)}
          </button>
        ))}
      </div>
      <div aria-live="polite" className="device-stack">
        <div className={`device-card device-desktop${active === "desktop" ? " is-active" : ""}`} data-testid="device-panel-desktop">
          <div className="device-frame desktop-frame tilt device-tilt" onMouseMove={tiltMove} onMouseLeave={tiltLeave}>
            <div className="screen-topbar"><span /><span /><span /></div>
            <div className="desktop-screen">
              <div className="screen-nav">
                <img alt="TransitionWorks" src={images.logoLight} />
                <div className="screen-links"><span>5Ps</span><span>eBook</span><span>Quiz</span></div>
              </div>
              <div className="screen-body">
                <div className="screen-copy"><small>Desktop experience</small><h4>Read comfortably on a larger screen.</h4><p>Ideal when you want the full picture and want to revisit several lessons in one sitting.</p></div>
                <img alt="From Campus to Corporate eBook cover" src={cover} />
              </div>
            </div>
          </div>
        </div>
        <div className={`device-card device-tablet${active === "tablet" ? " is-active" : ""}`} data-testid="device-panel-tablet">
          <div className="device-frame tablet-frame tilt device-tilt" onMouseMove={tiltMove} onMouseLeave={tiltLeave}>
            <div className="tablet-screen">
              <img alt="From Campus to Corporate eBook on tablet" src={cover} />
              <div className="tablet-overlay"><small>Tablet experience</small><h4>Balanced for browsing and note-taking.</h4><p>Great for reading on the sofa, during commutes, or when you want a bigger display without using a laptop.</p></div>
            </div>
          </div>
        </div>
        <div className={`device-card device-mobile${active === "mobile" ? " is-active" : ""}`} data-testid="device-panel-mobile">
          <div className="device-frame mobile-frame tilt device-tilt" onMouseMove={tiltMove} onMouseLeave={tiltLeave}>
            <div className="mobile-notch" />
            <div className="mobile-screen">
              <div className="mobile-reader"><small>Mobile experience</small><h4>Quick access on the go.</h4><p>Open a lesson when something relevant happens at work, then come back to the rest later.</p></div>
              <img alt="From Campus to Corporate eBook on mobile" src={cover} />
            </div>
          </div>
        </div>
        <div className="device-chip chip-one">Instant digital access</div>
        <div className="device-chip chip-two">Works across phone, tablet and desktop</div>
        <div className="device-chip chip-three">Built to revisit, not binge once</div>
      </div>
    </div>
  );
};

export const Offer = () => {
  const { offer, price } = useSite();
  return (
  <section className="offer-section" data-rail="The eBook" id="ebook" data-testid="offer-section">
    <div className="container">
      <div className="offer-card reveal">
        <div className="offer-layout">
          <div className="offer-copy">
            <span className="eyebrow">{offer.eyebrow}</span>
            <h2>{offer.headingPre}<span>{offer.headingHighlight}</span>{offer.headingPost}</h2>
            <p className="offer-lead">{offer.lead}</p>
            <div className="offer-benefits">
              {offer.benefits.map((t) => (
                <div className="benefit-item" key={t}><b>✓</b><span>{t}</span></div>
              ))}
            </div>
            <div className="offer-value-note">{offer.valueNote}</div>
          </div>
          <div className="offer-visuals">
            <DeviceShowcase />
            <div className="offer-price-panel" data-testid="offer-price-panel">
              <div className="price-kicker">{offer.priceKicker}</div>
              {offer.includeBadge && (
                <div className="include-badge" data-testid="offer-include-badge"><Check size={14} strokeWidth={3} /> {offer.includeBadge}</div>
              )}
              <div className="price-row">
                <div className="price-amount" data-testid="offer-price">{price}</div>
                <p>{offer.priceSub}</p>
              </div>
              <div className="price-highlight">
                <strong>Built to be revisited, not binge-read once.</strong>
                <p>Read one relevant lesson when you need it, then return as your situation changes.</p>
              </div>
              <div className="price-actions">
                <CheckoutButton className="btn btn-gold btn-block" testId="offer-checkout-btn">{offer.primaryCta}</CheckoutButton>
                {offer.trustLine && <div className="price-trust" data-testid="offer-trust-line"><ShieldCheck size={14} /> {offer.trustLine}</div>}
                <a className="offer-secondary-link" href="#sample" data-testid="offer-sample-link">Not sure? Read one lesson free →</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};
